import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { BookOpen, ExternalLink, Loader2 } from 'lucide-react';

const fmt = (v) =>
  `GHS ${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Tiny inline link that, when an auto-posted journal exists for a given
 * business event, shows "View posted journal · JE-2026-00xxx →".
 *
 * - source: 'sale' | 'purchase' | 'supplier_payment' | 'payroll' | 'sale_return'
 * - sourceRef: the original document id (sale_id, po_id, payment_id, run_id...)
 *
 * Renders nothing while loading or when no journal was posted (e.g. the
 * CoA hasn't been seeded for this tenant yet — the link gracefully hides).
 */
export default function GlAuditLink({ source, sourceRef, label }) {
  const [journal, setJournal] = useState(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!sourceRef) return undefined;
    (async () => {
      try {
        const { data } = await api.get('/ledger/journal-by-source', {
          params: { source, source_ref: sourceRef },
        });
        if (!cancelled) setJournal(data?.found ? data.journal : null);
      } catch (e) {
        // Permission denied or no CoA → silently hide
        console.warn('GL audit link unavailable:', e);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [source, sourceRef]);

  if (!checked || !journal) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-1.5 text-[11px] text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline"
        data-testid={`gl-audit-link-${source}`}
      >
        <BookOpen className="h-3 w-3" />
        <span>{label || 'View posted journal'} · </span>
        <span className="font-mono font-semibold">{journal.entry_no}</span>
        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>

      <JournalAuditDialog open={open} onClose={() => setOpen(false)} journal={journal} />
    </>
  );
}

function JournalAuditDialog({ open, onClose, journal }) {
  const j = journal || {};
  const totalDr = (j.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="gl-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            <span className="font-mono">{j.entry_no}</span>
            <StatusPill status={j.status} />
          </DialogTitle>
          <DialogDescription>
            {j.date} · {j.memo || j.source}
          </DialogDescription>
        </DialogHeader>

        {j.lines ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-right px-3 py-2 w-28">Debit</th>
                  <th className="text-right px-3 py-2 w-28">Credit</th>
                  <th className="text-left px-3 py-2 w-44">Description</th>
                </tr>
              </thead>
              <tbody>
                {j.lines.map((ln) => (
                  <tr key={`${ln.account_code || 'line'}-${ln.description || ''}-${ln.debit || ''}-${ln.credit || ''}`} className="border-t">
                    <td className="px-3 py-2">
                      <span className="font-mono mr-2 text-muted-foreground">{ln.account_code}</span>
                      {ln.account_name}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {ln.debit ? fmt(ln.debit) : ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {ln.credit ? fmt(ln.credit) : ''}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {ln.description || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-semibold">
                <tr>
                  <td className="px-3 py-2 text-right">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(totalDr)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(totalDr)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="outline" asChild>
            <a href="/finance/ledger/journals" data-testid="gl-audit-open-page">
              <ExternalLink className="h-4 w-4 mr-1.5" /> Open in Ledger
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  const cls =
    status === 'posted'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'reversed'
        ? 'bg-slate-200 text-slate-700'
        : 'bg-amber-100 text-amber-700';
  return <Badge className={cls}>{status}</Badge>;
}
